
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Category, ExpenseItem, CURRENCIES, formatDateForStorage, formatDateForInput } from "@/types/ExpenseReport";

export interface EditExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (expense: Partial<ExpenseItem>) => void;
  expense?: Partial<ExpenseItem>;
  categories: Category[];
  organizationId: string;
  isNew?: boolean;
  reportCurrency: string;
}

export const EditExpenseDialog = ({ 
  open, 
  onClose, 
  onSave, 
  expense = {}, 
  categories,
  organizationId,
  isNew = false,
  reportCurrency
}: EditExpenseDialogProps) => {
  const today = formatDateForStorage(new Date());
  const [description, setDescription] = useState(expense.description || '');
  const [amount, setAmount] = useState(expense.amount?.toString() || '');
  const [date, setDate] = useState(expense.date ? formatDateForInput(expense.date) : today);
  const [categoryId, setCategoryId] = useState(expense.category_id?.toString() || '');
  const [currency, setCurrency] = useState(expense.currency || reportCurrency || 'USD');
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setDescription(expense.description || '');
      setAmount(expense.amount?.toString() || '');
      setDate(expense.date ? formatDateForInput(expense.date) : today);
      setCategoryId(expense.category_id?.toString() || '');
      setCurrency(expense.currency || reportCurrency || 'USD');
    }
  }, [expense, open, reportCurrency, today]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!description || !amount || !categoryId || !date) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }
    
    onSave({
      ...expense,
      description,
      amount: parseFloat(amount),
      category_id: parseInt(categoryId, 10),
      date, // This will be in YYYY-MM-DD format
      currency,
      organization_id: organizationId,
    });
    
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add Expense" : "Edit Expense"}</DialogTitle>
          <DialogDescription>
            {isNew ? "Add a new expense to this report" : "Update the expense details"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                Description
              </Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="amount" className="text-right">
                Amount
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="date" className="text-right">
                Date
              </Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="category" className="text-right">
                Category
              </Label>
              <div className="col-span-3">
                <Select
                  value={categoryId}
                  onValueChange={setCategoryId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(category => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="currency" className="text-right">
                Currency
              </Label>
              <div className="col-span-3">
                <Select
                  value={currency}
                  onValueChange={setCurrency}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map(curr => (
                      <SelectItem key={curr.code} value={curr.code}>
                        {curr.code} ({curr.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">{isNew ? "Add" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
